import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import styled, { createGlobalStyle } from "styled-components";
import { Dialog } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import useCheckoutDeliverySchedule from "../../hooks/useCheckoutDeliverySchedule";
import DeliveryTimePicker from "../DeliveryTimePicker";
import { earliestGroupWindow, groupScheduleLeadMinutes } from "../../utils/groupBasketSchedule";
import useMediaQuery from "../../hooks/useMediaQuery";
import { createGroupBasket } from "../../api/groupBasketApi";
import { trackGroupBasketCreate } from "../../utils/analytics";
import { groupBasketPath } from "../../utils/groupBasketLink";
import { MODE, MODE_LABELS, cutoffChoices, formatMinutes } from "../../utils/groupBasketView";
import { isValidDeliveryPhone } from "../../utils/shippingValidation";
import { resolveInitialDeliveryPhone, saveLastDeliveryPhone } from "../../utils/deliveryPhone";
import { isWithinMarketBounds, normalizeMarketLatLng } from "../../config/market";
import { forgetMyGroupBaskets } from "../../hooks/useMyGroupBaskets";
import GroupDropoffPicker from "./GroupDropoffPicker";
import {
  ChipChoice,
  Choice,
  ChoiceGrid,
  FieldLabel,
  IconCircle,
  Notice,
  PrimaryButton,
  QuietButton,
  SectionLabel,
  SheetBody,
  SheetFoot,
  SheetHead,
  TextInput,
  sheetPaperProps,
} from "./groupBasketStyles";

const MODE_DETAILS = {
  [MODE.EACH_PAYS_SHARE]:
    "Each person pays for their own items and an equal part of delivery, by mobile money or card.",
  [MODE.HOST_PAYS_ALL]: "You place and pay for one order. Friends only add their items.",
};

const firstNameOf = (user) =>
  String(user?.result?.name || user?.name || "")
    .trim()
    .split(/\s+/)[0]
    .slice(0, 24);

/* The drop-off to start from: the delivery area the shopper already set. */
const initialDropoff = (user, userLocation) => {
  const lat = userLocation?.latitude;
  const lng = userLocation?.longitude;
  const latLng = isWithinMarketBounds(lat, lng) ? normalizeMarketLatLng(lat, lng) : null;
  return {
    address: latLng ? userLocation?.address || "" : "",
    description: "",
    latLng: latLng && userLocation?.address ? latLng : null,
    customerNumber: resolveInitialDeliveryPhone(user),
    source: userLocation?.locationSource === "gps" ? "gps" : "places",
    savedAddressId: null,
  };
};

/* The dropoff body for POST /api/group-baskets (an order's shippingAddress). */
export const dropoffPayload = (dropoff) => ({
  address: String(dropoff.address || "").trim(),
  description: String(dropoff.description || "").trim(),
  latLng: dropoff.latLng,
  customerNumber: String(dropoff.customerNumber || "").trim(),
  source: dropoff.source === "gps" ? "gps" : "places",
});

/*
 * Open an Order together basket at this restaurant: the drop-off, when the
 * basket closes, and who pays. On success the host lands on the basket with
 * the share card first.
 */
const GroupBasketCreateDialog = ({ open, onClose, vendorId, outletId, restaurantName, windows, scheduling }) => {
  const history = useHistory();
  const fullScreen = useMediaQuery("(max-width: 600px)");
  const user = useSelector((state) => state.user?.currentUser);
  const userLocation = useSelector((state) => state.userLocation);
  const [dropoff, setDropoff] = useState(() => initialDropoff(user, userLocation));
  const choices = useMemo(() => cutoffChoices(windows?.cutoffMinutes), [windows]);
  const [cutoff, setCutoff] = useState(() => Number(windows?.cutoffMinutes?.default) || null);
  const [mode, setMode] = useState(MODE.EACH_PAYS_SHARE);
  const earliestWindowStart = useMemo(() => earliestGroupWindow({ mode, cutoffMinutes: cutoff, windows, scheduling }), [mode, cutoff, windows, scheduling]);
  const timing = useCheckoutDeliverySchedule({ flow: "restaurant", vendorId, outletId, enabled: scheduling?.supported === true, earliestWindowStart });
  const [hostLabel, setHostLabel] = useState(() => firstNameOf(user));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const dropoffReady =
    Boolean(String(dropoff.address || "").trim()) &&
    Boolean(normalizeMarketLatLng(dropoff.latLng?.lat, dropoff.latLng?.lng)) &&
    isValidDeliveryPhone(dropoff.customerNumber);

  const missing = !dropoff.latLng
    ? "Choose where to deliver"
    : !isValidDeliveryPhone(dropoff.customerNumber)
      ? "Add the rider's contact number"
      : scheduling?.supported && timing.needsWindow
        ? "Choose a delivery window"
        : null;

  const submit = async () => {
    if (!dropoffReady || busy) return;
    if (scheduling?.supported && timing.validateSelection()) return;
    if (scheduling?.supported && timing.scheduled && new Date(timing.deliverySchedule.windowStart).getTime() < Date.now() + groupScheduleLeadMinutes({ mode, cutoffMinutes: cutoff, windows, scheduling }) * 60000) {
      setError({ message: "Choose a later delivery window so everyone has time to add items and complete payment." });
      timing.pickAnotherTime();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const label = hostLabel.trim();
      const basket = await createGroupBasket({
        vertical: "restaurant",
        vendorId,
        outletId: outletId || null,
        mode,
        ...(scheduling?.supported ? { deliverySchedule: timing.deliverySchedule } : {}),
        ...(cutoff ? { cutoffMinutes: cutoff } : {}),
        ...(label ? { hostLabel: label.slice(0, 24) } : {}),
        dropoff: dropoffPayload(dropoff),
      });
      saveLastDeliveryPhone(dropoff.customerNumber);
      forgetMyGroupBaskets();
      trackGroupBasketCreate({ mode, surface: "storefront" });
      history.push(`${groupBasketPath(basket.id)}?created=1`);
    } catch (err) {
      setError(err);
      if (scheduling?.supported) {
        if (err.code === "GROUP_DELIVERY_TOO_SOON") timing.pickAnotherTime();
        else timing.handleRejection(err.code, err.message);
      }
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={busy ? undefined : onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      disableEnforceFocus
      PaperProps={fullScreen ? undefined : sheetPaperProps}
    >
      {/* The address suggestions are portalled to the page: lift them above this sheet. */}
      <SuggestionsAboveSheet />
      <SheetHead>
        <div>
          <h2>Order together</h2>
          <p>One order from {restaurantName || "this restaurant"}, one rider, one drop-off.</p>
        </div>
        <IconCircle type="button" onClick={onClose} aria-label="Close" disabled={busy}>
          <CloseIcon />
        </IconCircle>
      </SheetHead>
      <SheetBody>
        <section>
          <SectionLabel>Where should we deliver?</SectionLabel>
          <GroupDropoffPicker value={dropoff} onChange={setDropoff} autoSelectSaved={!dropoff.latLng} />
        </section>

        {scheduling?.supported && (
          <section>
            <DeliveryTimePicker controller={timing} />
            <Notice>Later windows leave time for everyone to choose their items and pay. The basket cut-off is separate from delivery time.</Notice>
          </section>
        )}
        <section>
          <SectionLabel>When does the basket close?</SectionLabel>
          <ChoiceGrid $columns={3}>
            {choices.map((minutes) => (
              <ChipChoice
                key={minutes}
                type="button"
                $selected={cutoff === minutes}
                aria-pressed={cutoff === minutes}
                onClick={() => setCutoff(minutes)}
              >
                <strong>{formatMinutes(minutes)}</strong>
              </ChipChoice>
            ))}
          </ChoiceGrid>
          <Hint>Friends add items until then. You can lock it sooner.</Hint>
        </section>

        <section>
          <SectionLabel>Who pays?</SectionLabel>
          <ChoiceGrid>
            {[MODE.EACH_PAYS_SHARE, MODE.HOST_PAYS_ALL].map((value) => (
              <Choice
                key={value}
                type="button"
                $selected={mode === value}
                aria-pressed={mode === value}
                onClick={() => setMode(value)}
              >
                <strong>{MODE_LABELS[value]}</strong>
                <span>{MODE_DETAILS[value]}</span>
              </Choice>
            ))}
          </ChoiceGrid>
        </section>

        <section>
          <FieldLabel htmlFor="group-host-label">Your name in the basket</FieldLabel>
          <TextInput
            id="group-host-label"
            value={hostLabel}
            maxLength={24}
            onChange={(e) => setHostLabel(e.target.value)}
            placeholder="Your first name"
          />
        </section>

        {error && (
          <Notice $tone="danger" role="alert">
            <span>
              {error.message}
              {error.code === "HOST_HAS_ACTIVE_BASKET" && error.extra?.basketId ? (
                <>
                  {" "}
                  <QuietButton
                    type="button"
                    style={{ marginTop: 10 }}
                    onClick={() => history.push(groupBasketPath(error.extra.basketId))}
                  >
                    Open my basket
                  </QuietButton>
                </>
              ) : null}
            </span>
          </Notice>
        )}
      </SheetBody>
      <SheetFoot>
        <PrimaryButton type="button" onClick={submit} disabled={!dropoffReady || busy}>
          {busy ? "Opening the basket" : missing || "Open the basket"}
        </PrimaryButton>
      </SheetFoot>
    </Dialog>
  );
};

const SuggestionsAboveSheet = createGlobalStyle`
  [data-reach-combobox-popover] {
    z-index: 1400 !important;
  }
`;

const Hint = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  color: #55646a;
`;

export default GroupBasketCreateDialog;
